/**
 * THE LOADER -- the brand gem (`RubyGemMark`) glowing, sparks firing along
 * its facets (`RubyGemGlow`, the same files as the POS till and platform-app),
 * also the Ruby AI "thinking" state. Every waiting state in
 * the admin is this mark, not a generic spinner. Transparent, so it sits on
 * the light body and the black chrome alike.
 *
 *   `screen`  -- the whole app is booting / a blocking overlay. Full viewport.
 *   `page`    -- a route / tab body is loading.
 *   `section` -- a panel, card or list is loading.
 *   `overlay` -- centred over a skeleton (parent must be `relative`).
 *   `inline`  -- the bare gem sized by `size`, for dropping into a row.
 *
 * Buttons / tiny spinners: `<RubyGemMark size={same px} drawing color="currentColor" />`.
 * No `animate-spin` spinner and no bare skeleton ships anywhere -- this is the rule.
 */
import React from 'react';
import { RubyGemMark } from './RubyGemMark';

export interface RubyLoaderProps {
  variant?: 'screen' | 'page' | 'section' | 'inline' | 'overlay';
  /** Overrides the variant's default gem size (px). */
  size?: number;
  /** Announced to screen readers only -- the loader is the animation alone, no text, no backdrop. */
  label?: React.ReactNode;
  className?: string;
}

const DEFAULT_SIZE = { screen: 88, page: 64, section: 44, inline: 20, overlay: 56 } as const;

export const RubyLoader: React.FC<RubyLoaderProps> = ({ variant = 'page', size, label, className = '' }) => {
  const px = size ?? DEFAULT_SIZE[variant];
  const gem = <RubyGemMark size={px} drawing />;

  // Over a skeleton: the grey shapes hold the layout, the gem says "loading".
  // The parent must be `relative`.
  if (variant === 'overlay') {
    return (
      <div role="status" aria-live="polite" className={`absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 pointer-events-none ${className}`}>
        {gem}
        <span className="sr-only">{label || 'Loading'}</span>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <span role="status" aria-live="polite" className={`inline-flex items-center gap-2 ${className}`}>
        {gem}
        <span className="sr-only">{label || 'Loading'}</span>
      </span>
    );
  }

  const box =
    variant === 'screen' ? 'min-h-screen w-full gap-4'
    : variant === 'page' ? 'w-full py-20 gap-3'
    : 'w-full py-8 gap-2';

  return (
    <div role="status" aria-live="polite" className={`flex flex-col items-center justify-center ${box} ${className}`}>
      {gem}
      <span className="sr-only">{label || 'Loading'}</span>
    </div>
  );
};

export default RubyLoader;
